import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Row, Icon } from '@/components/ui/primitives';
import { MobileInput, ISSMap } from '@/components/ui/widgets';
import { Autocomplete } from '@/components/ui';
import { lockActiveIcon, lockIcon, navigation } from '@/assets';
import { useEffect, useRef } from 'react';

interface MapSectionProps {
  mapKey: string;
  coordinates: { lat: number; lng: number };
  latitudeInput: string;
  longitudeInput: string;
  isActiveLock: boolean;
  isFullscreen: boolean;
  isGettingLocation: boolean;
  updateStatus: {
    coordinates: { error: boolean; updating: boolean; updated: boolean };
  };
  onChangeCoordinates: (coords: { lat: number; lng: number }) => void;
  onChangeLatitude: (value: string) => void;
  onChangeLongitude: (value: string) => void;
  setValidLatitude: () => void;
  setValidLongitude: () => void;
  toggleLock: () => void;
  getCoordinates: () => void;
  createMapLink: (lat: string, lng: string) => string;
}

export const MapSection: React.FC<MapSectionProps> = ({
  mapKey,
  coordinates,
  latitudeInput,
  longitudeInput,
  isActiveLock,
  isFullscreen,
  isGettingLocation,
  updateStatus,
  onChangeCoordinates,
  onChangeLatitude,
  onChangeLongitude,
  setValidLatitude,
  setValidLongitude,
  toggleLock,
  getCoordinates,
  createMapLink,
}) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      // Handle fullscreen change if needed
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!mapKey) {
    return null;
  }

  return (
    <>
      {/* Google Map Autocomplete */}
      <Row alignItems={'flex-end'}>
        <Box width={'100%'}>
          <Autocomplete
            getCoordinates={getCoordinates}
            mapKey={mapKey}
            onChangeCoordinates={onChangeCoordinates}
            disabled={isActiveLock}
            isGettingLocation={isGettingLocation}
          />
        </Box>

        <Box
          onClick={toggleLock}
          sx={{
            mx: '0.75rem',
            cursor: 'pointer',
            height: '2.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            src={isActiveLock ? lockActiveIcon : lockIcon}
            sx={{ width: '2rem', height: '2.5rem' }}
          />
        </Box>
      </Row>

      {/* Map Container */}
      <Box
        ref={mapContainerRef}
        sx={{
          top: 0,
          left: 0,
          height: '21.5625rem',
          position: isFullscreen ? 'fixed' : 'relative',
          width: '100%',
        }}
      >
        <ISSMap
          mapKey={mapKey}
          disabled={updateStatus.coordinates.updating}
          value={coordinates}
          onChangeCoordinates={onChangeCoordinates}
          isActiveLock={isActiveLock}
          isFullscreen={isFullscreen}
        />
      </Box>

      {/* Latitude and Longitude Inputs */}
      <Box sx={{ display: 'flex', width: '100%' }} alignItems={'flex-end'}>
        <Box width='50%'>
          <MobileInput
            label={`${t('latitude_edit_site')}`}
            name='latitude'
            helperText={''}
            onChange={value => onChangeLatitude(value.target.value)}
            blur={setValidLatitude}
            value={latitudeInput}
            updated={updateStatus.coordinates.updated}
            disabled={updateStatus.coordinates.updating}
            error={updateStatus.coordinates.error}
          />
        </Box>
        <Box sx={{ width: '1.25rem' }} />
        <Box width='50%'>
          <MobileInput
            label={`${t('longitude_edit_site')}`}
            name='longitude'
            helperText={''}
            onChange={value => onChangeLongitude(value.target.value)}
            blur={setValidLongitude}
            value={longitudeInput}
            updated={updateStatus.coordinates.updated}
            disabled={updateStatus.coordinates.updating}
            error={updateStatus.coordinates.error}
          />
        </Box>
        <a
          style={{ display: 'flex' }}
          href={`${createMapLink(latitudeInput, longitudeInput)}`}
        >
          <Icon sx={{ mx: '0.75rem' }} src={navigation} />
        </a>
      </Box>
    </>
  );
};
